/**
 * Utility functions for applying branding to pages
 */

import { BrandingConfig } from './branding'

/**
 * Get common page styling with branding applied
 */
export function getPageStyles(branding: BrandingConfig) {
  return {
    container: {
      backgroundColor: branding.backgroundColor,
      color: branding.textColor,
    },
    surface: {
      backgroundColor: branding.surfaceColor,
      color: branding.textColor,
    },
    text: {
      color: branding.textColor,
    },
    textSecondary: {
      color: branding.textSecondaryColor,
    },
    primary: {
      color: branding.primaryColor,
    },
    primaryBg: {
      backgroundColor: branding.primaryColor,
    },
    primaryGradient: {
      background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
    },
    secondaryGradient: {
      background: `linear-gradient(135deg, ${branding.secondaryColor} 0%, ${branding.secondaryColorLight} 100%)`,
    },
    border: {
      borderColor: `${branding.textSecondaryColor}20`,
    },
  }
}

/**
 * Get branded card styles
 */
export function getCardStyles(branding: BrandingConfig) {
  return {
    backgroundColor: branding.surfaceColor,
    borderColor: `${branding.textSecondaryColor}20`,
    color: branding.textColor,
  }
}

/**
 * Get branded button styles
 */
export function getButtonStyles(branding: BrandingConfig, variant: 'primary' | 'secondary' | 'outline' = 'primary') {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: branding.primaryColor,
        color: branding.textColor,
        '--hover-bg': branding.primaryColorDark,
      }
    case 'secondary':
      return {
        backgroundColor: branding.secondaryColor,
        color: branding.textColor,
        '--hover-bg': branding.secondaryColorDark,
      }
    case 'outline':
      return {
        backgroundColor: 'transparent',
        color: branding.primaryColor,
        borderColor: branding.primaryColor,
        '--hover-bg': `${branding.primaryColor}10`,
      }
    default:
      return {}
  }
}

/**
 * Get branded input styles
 */
export function getInputStyles(branding: BrandingConfig) {
  return {
    backgroundColor: `${branding.surfaceColor}80`,
    borderColor: `${branding.textSecondaryColor}30`,
    color: branding.textColor,
    '--placeholder-color': branding.textSecondaryColor,
    '--focus-border': branding.primaryColor,
  }
}

/**
 * Get branded table styles
 */
export function getTableStyles(branding: BrandingConfig) {
  return {
    backgroundColor: branding.surfaceColor,
    borderColor: `${branding.textSecondaryColor}20`,
    headerBg: `${branding.backgroundColor}80`,
    rowHover: `${branding.primaryColor}05`,
    textColor: branding.textColor,
    secondaryTextColor: branding.textSecondaryColor,
  }
}

/**
 * Get branded status colors
 */
export function getStatusColors(branding: BrandingConfig) {
  return {
    success: {
      color: '#10b981',
      backgroundColor: '#10b98120',
      borderColor: '#10b98140',
    },
    warning: {
      color: '#f59e0b',
      backgroundColor: '#f59e0b20',
      borderColor: '#f59e0b40',
    },
    error: {
      color: '#ef4444',
      backgroundColor: '#ef444420',
      borderColor: '#ef444440',
    },
    info: {
      color: branding.primaryColor,
      backgroundColor: `${branding.primaryColor}20`,
      borderColor: `${branding.primaryColor}40`,
    },
    pending: {
      color: '#6b7280',
      backgroundColor: '#6b728020',
      borderColor: '#6b728040',
    },
  }
}
